/**
 * Metarr-Entwürfe — Herleitung und Aktionsarten (Paket 9).
 *
 * Nichts wird geraten. Herleitung muss wörtlich im Satz vorkommen.
 */
import { z } from "zod";
import { aufgabenartSchema } from "./aufgaben.js";
export const INTERNE_AKTIONEN = [
    "task_update",
    "clarification_resolve",
    "clarification_reopen",
];
/** Aktionsarten, für die der Server bereits einen atomaren Fachschreibpfad hat. */
export const AUSFUEHRBARE_AKTIONEN = [
    "task_create",
    "aufgabe_anlegen",
    "akzeptanzkriterium_anlegen",
    "seite_anlegen",
];
export const METARR_ARBEITSABLAEUFE = [
    "akzeptanzkriterien_vorschlagen",
    "projektplanung",
];
/* -------------------------------------------------------------------------
 * Projektpläne
 * ---------------------------------------------------------------------- */
const lokaleKennungSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/);
const datumSchema = z.union([
    z.iso.date(),
    z.iso.datetime({ offset: true }),
]);
const projektplanQuelleSchema = z
    .object({
    art: z.enum(["projekt", "aufgabe", "meilenstein", "kommentar", "seite"]),
    id: z.uuid(),
    beleg: z.string().trim().min(1).max(500),
})
    .strict();
const objektReferenzSchema = z.union([
    z.object({ id: z.uuid() }).strict(),
    z.object({ ausAktion: lokaleKennungSchema }).strict(),
]);
const gemeinsamePlanaktion = {
    lokaleKennung: lokaleKennungSchema,
    voraussetzungen: z.array(lokaleKennungSchema).max(20),
    begruendung: z.string().trim().min(1).max(2_000),
    quellen: z.array(projektplanQuelleSchema).max(10),
};
const meilensteinAnlegenAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("meilenstein_anlegen"),
    argumente: z
        .object({
        name: z.string().trim().min(1).max(200),
        faelligAm: datumSchema,
        beschreibung: z.string().max(5_000).optional(),
    })
        .strict(),
})
    .strict();
const meilensteinAendernAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("meilenstein_aendern"),
    argumente: z
        .object({
        meilensteinId: z.uuid(),
        erwarteteVersion: z.number().int().positive(),
        name: z.string().trim().min(1).max(200).optional(),
        faelligAm: datumSchema.optional(),
        beschreibung: z.string().max(5_000).optional(),
    })
        .strict()
        .refine((argumente) => argumente.name !== undefined ||
        argumente.faelligAm !== undefined ||
        argumente.beschreibung !== undefined, "Eine Meilensteinänderung braucht mindestens ein geändertes Feld."),
})
    .strict();
const meilensteinArchivierenAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("meilenstein_archivieren"),
    argumente: z
        .object({
        meilensteinId: z.uuid(),
        erwarteteVersion: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
const aufgabeAnlegenAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("aufgabe_anlegen"),
    argumente: z
        .object({
        titel: z.string().trim().min(1).max(500),
        inhalt: z.string().max(100_000).optional(),
        aufgabenart: aufgabenartSchema.optional(),
        zustandId: z.uuid().optional(),
        prioritaet: z.number().int().min(0).max(3).optional(),
        faelligAm: datumSchema.nullable().optional(),
        schaetzungMinuten: z.number().int().positive().max(1_000_000).nullable().optional(),
        meilenstein: objektReferenzSchema.nullable().optional(),
        elternAufgabe: objektReferenzSchema.optional(),
    })
        .strict(),
})
    .strict();
const aufgabeAendernAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("aufgabe_aendern"),
    argumente: z
        .object({
        aufgabeId: z.uuid(),
        erwarteteVersion: z.number().int().positive(),
        titel: z.string().trim().min(1).max(500).optional(),
        inhalt: z.string().max(100_000).optional(),
        aufgabenart: aufgabenartSchema.optional(),
        prioritaet: z.number().int().min(0).max(3).optional(),
        faelligAm: datumSchema.nullable().optional(),
        schaetzungMinuten: z.number().int().positive().max(1_000_000).nullable().optional(),
    })
        .strict()
        .refine((argumente) => argumente.titel !== undefined ||
        argumente.inhalt !== undefined ||
        argumente.aufgabenart !== undefined ||
        argumente.prioritaet !== undefined ||
        argumente.faelligAm !== undefined ||
        argumente.schaetzungMinuten !== undefined, "Eine Aufgabenänderung braucht mindestens ein geändertes Feld."),
})
    .strict();
const aufgabeMeilensteinSetzenAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("aufgabe_meilenstein_setzen"),
    argumente: z
        .object({
        aufgabe: objektReferenzSchema,
        meilenstein: objektReferenzSchema.nullable(),
        erwarteteVersion: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
const aufgabeStatusSetzenAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("aufgabe_status_setzen"),
    argumente: z
        .object({
        aufgabe: objektReferenzSchema,
        zustandId: z.uuid(),
        erwarteteVersion: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
const aufgabeZuweisenAktion = z
    .object({
    ...gemeinsamePlanaktion,
    art: z.literal("aufgabe_zuweisen"),
    argumente: z
        .object({
        aufgabe: objektReferenzSchema,
        personIds: z.array(z.uuid()).max(20),
        erwarteteVersion: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
export const projektplanAktionSchema = z.discriminatedUnion("art", [
    meilensteinAnlegenAktion,
    meilensteinAendernAktion,
    meilensteinArchivierenAktion,
    aufgabeAnlegenAktion,
    aufgabeAendernAktion,
    aufgabeMeilensteinSetzenAktion,
    aufgabeStatusSetzenAktion,
    aufgabeZuweisenAktion,
]);
function ausAktionenIn(wert, ergebnis) {
    if (Array.isArray(wert)) {
        for (const eintrag of wert)
            ausAktionenIn(eintrag, ergebnis);
        return;
    }
    if (typeof wert !== "object" || wert === null)
        return;
    const objekt = wert;
    if (typeof objekt["ausAktion"] === "string") {
        ergebnis.add(objekt["ausAktion"]);
    }
    for (const eintrag of Object.values(objekt))
        ausAktionenIn(eintrag, ergebnis);
}
/**
 * Liefert eine stabile topologische Reihenfolge und weist unauflösbare Pläne
 * fail-closed ab. Die Eingabereihenfolge entscheidet zwischen gleichrangigen
 * Aktionen, damit Vorschau und Ausführung denselben Plan zeigen.
 */
export function sortierePlanAktionen(aktionen) {
    const nachKennung = new Map();
    for (const aktion of aktionen) {
        if (nachKennung.has(aktion.lokaleKennung)) {
            throw new Error(`Doppelte lokale Kennung: ${aktion.lokaleKennung}.`);
        }
        nachKennung.set(aktion.lokaleKennung, aktion);
    }
    for (const aktion of aktionen) {
        const gesehen = new Set();
        for (const voraussetzung of aktion.voraussetzungen) {
            if (gesehen.has(voraussetzung)) {
                throw new Error(`Doppelte Voraussetzung ${voraussetzung} an ${aktion.lokaleKennung}.`);
            }
            gesehen.add(voraussetzung);
            if (!nachKennung.has(voraussetzung)) {
                throw new Error(`Unbekannte Voraussetzung ${voraussetzung} an ${aktion.lokaleKennung}.`);
            }
            if (voraussetzung === aktion.lokaleKennung) {
                throw new Error(`Aktion ${aktion.lokaleKennung} setzt sich selbst voraus.`);
            }
        }
    }
    const offen = new Map(aktionen.map((aktion) => [aktion.lokaleKennung, aktion.voraussetzungen.length]));
    const sortiert = [];
    const erledigt = new Set();
    while (sortiert.length < aktionen.length) {
        const naechste = aktionen.filter((aktion) => !erledigt.has(aktion.lokaleKennung) &&
            offen.get(aktion.lokaleKennung) === 0);
        if (naechste.length === 0) {
            throw new Error("Der Projektplan enthält einen Abhängigkeitszyklus.");
        }
        for (const aktion of naechste) {
            erledigt.add(aktion.lokaleKennung);
            sortiert.push(aktion);
            for (const kandidat of aktionen) {
                if (kandidat.voraussetzungen.includes(aktion.lokaleKennung)) {
                    offen.set(kandidat.lokaleKennung, (offen.get(kandidat.lokaleKennung) ?? 0) - 1);
                }
            }
        }
    }
    return sortiert;
}
/** Schließt eine Auswahl transitiv über alle noch benötigten Voraussetzungen. */
export function schliessePlanAuswahl(aktionen, ausgewaehlt) {
    const sortiert = sortierePlanAktionen(aktionen);
    const nachKennung = new Map(sortiert.map((aktion) => [aktion.lokaleKennung, aktion]));
    const geschlossen = new Set();
    const besuche = (kennung) => {
        const aktion = nachKennung.get(kennung);
        if (!aktion)
            throw new Error(`Unbekannte ausgewählte Aktion: ${kennung}.`);
        if (geschlossen.has(kennung))
            return;
        for (const voraussetzung of aktion.voraussetzungen)
            besuche(voraussetzung);
        geschlossen.add(kennung);
    };
    for (const kennung of ausgewaehlt)
        besuche(kennung);
    return sortiert
        .map((aktion) => aktion.lokaleKennung)
        .filter((kennung) => geschlossen.has(kennung));
}
export const projektplanSchema = z
    .object({
    projektId: z.uuid(),
    titel: z.string().trim().min(1).max(200),
    zusammenfassung: z.string().trim().min(1).max(5_000),
    aktionen: z.array(projektplanAktionSchema).min(1).max(100),
})
    .strict()
    .superRefine((plan, kontext) => {
    try {
        sortierePlanAktionen(plan.aktionen);
    }
    catch (fehler) {
        kontext.addIssue({
            code: "custom",
            path: ["aktionen"],
            message: fehler instanceof Error ? fehler.message : "Ungültiger Plan.",
        });
        return;
    }
    const nachKennung = new Map(plan.aktionen.map((aktion) => [aktion.lokaleKennung, aktion]));
    plan.aktionen.forEach((aktion, index) => {
        const referenzen = new Set();
        ausAktionenIn(aktion.argumente, referenzen);
        for (const referenz of referenzen) {
            if (!aktion.voraussetzungen.includes(referenz)) {
                kontext.addIssue({
                    code: "custom",
                    path: ["aktionen", index, "voraussetzungen"],
                    message: `Ergebnisreferenz ${referenz} muss eine direkte Voraussetzung sein.`,
                });
                continue;
            }
            const erzeuger = nachKennung.get(referenz);
            if (!erzeuger)
                continue;
            const brauchtMeilenstein = (aktion.art === "aufgabe_anlegen" &&
                typeof aktion.argumente.meilenstein === "object" &&
                aktion.argumente.meilenstein !== null &&
                "ausAktion" in aktion.argumente.meilenstein &&
                aktion.argumente.meilenstein.ausAktion === referenz) ||
                (aktion.art === "aufgabe_meilenstein_setzen" &&
                    typeof aktion.argumente.meilenstein === "object" &&
                    aktion.argumente.meilenstein !== null &&
                    "ausAktion" in aktion.argumente.meilenstein &&
                    aktion.argumente.meilenstein.ausAktion === referenz);
            if (brauchtMeilenstein && erzeuger.art !== "meilenstein_anlegen") {
                kontext.addIssue({
                    code: "custom",
                    path: ["aktionen", index, "argumente"],
                    message: `${referenz} erzeugt keinen Meilenstein.`,
                });
            }
            const brauchtAufgabe = (aktion.art === "aufgabe_anlegen" &&
                typeof aktion.argumente.elternAufgabe === "object" &&
                "ausAktion" in aktion.argumente.elternAufgabe &&
                aktion.argumente.elternAufgabe.ausAktion === referenz) ||
                [
                    "aufgabe_meilenstein_setzen",
                    "aufgabe_status_setzen",
                    "aufgabe_zuweisen",
                ].includes(aktion.art) &&
                    "aufgabe" in aktion.argumente &&
                    "ausAktion" in aktion.argumente.aufgabe &&
                    aktion.argumente.aufgabe.ausAktion === referenz;
            if (brauchtAufgabe && erzeuger.art !== "aufgabe_anlegen") {
                kontext.addIssue({
                    code: "custom",
                    path: ["aktionen", index, "argumente"],
                    message: `${referenz} erzeugt keine Aufgabe.`,
                });
            }
        }
    });
});
/** Fortschrittsstufen je Weg — keine vierte Stufe erfinden, wo der Mechanismus keine hergibt. */
export const FORTSCHRITT_WARTESCHLANGE = [
    "wartet",
    "abgeholt",
    "schreibt",
    "vorschlag",
];
export const FORTSCHRITT_BEIWAGEN = ["wartet", "schreibt", "vorschlag"];
function normalisiere(s) {
    return s.toLowerCase().replace(/\s+/g, " ").trim();
}
/**
 * Prüft die messbaren Qualitätsbedingungen eines vorgeschlagenen
 * Akzeptanzkriteriums. Inhalte werden ausschließlich als Daten behandelt;
 * entscheidend sind Zielkennung und wörtlich nachweisbare Belege.
 */
export function pruefeAkzeptanzkriteriumVorschlag(optionen) {
    const maengel = [];
    const text = optionen.text.trim();
    if (text.length === 0) {
        maengel.push("Akzeptanzkriterium braucht einen Text.");
    }
    else if (text.length > 1_000) {
        maengel.push("Akzeptanzkriterium darf höchstens 1000 Zeichen lang sein.");
    }
    if (optionen.bestehendeKriterien?.some((bestehend) => normalisiere(bestehend) === normalisiere(text))) {
        maengel.push("Akzeptanzkriterium ist bereits vorhanden.");
    }
    if (optionen.quellen.length === 0) {
        maengel.push("Akzeptanzkriterium braucht mindestens eine belegte Quelle.");
    }
    if (optionen.quellen.length > 10) {
        maengel.push("Akzeptanzkriterium darf höchstens zehn Quellen nennen.");
    }
    for (const quelle of optionen.quellen) {
        const beleg = quelle.beleg.trim();
        if (beleg.length === 0) {
            maengel.push(`Quelle ${quelle.art}:${quelle.id} hat keinen Beleg.`);
            continue;
        }
        if (beleg.length > 500) {
            maengel.push(`Quelle ${quelle.art}:${quelle.id} hat einen zu langen Beleg.`);
            continue;
        }
        if (quelle.art === "aufgabe" && quelle.id !== optionen.zielAufgabeId) {
            maengel.push("Aufgabenquelle gehört nicht zur Zielaufgabe.");
            continue;
        }
        const quelltext = optionen.quelltexte.find((kandidat) => kandidat.art === quelle.art && kandidat.id === quelle.id);
        if (!quelltext) {
            maengel.push(`Quelle ${quelle.art}:${quelle.id} ist nicht sichtbar.`);
            continue;
        }
        if (!normalisiere(quelltext.text).includes(normalisiere(beleg))) {
            maengel.push(`Beleg für ${quelle.art}:${quelle.id} steht dort nicht wörtlich.`);
        }
    }
    return maengel;
}
/** Beleg muss wörtlich (normalisiert) im Satz vorkommen. */
export function pruefeHerleitung(satz, beleg) {
    if (beleg === null || beleg === "")
        return false;
    return normalisiere(satz).includes(normalisiere(beleg));
}
export function pruefeFeld(feld) {
    const maengel = [];
    if (feld.wert === null && !feld.grund) {
        maengel.push("Feld ohne Wert braucht einen Grund.");
    }
    if (feld.beleg !== null && feld.beleg !== "" && feld.wert !== null) {
        // beleg must be validated against satz by caller
    }
    return maengel;
}
export function pruefeVorschlag(optionen) {
    const maengel = [];
    if (!AUSFUEHRBARE_AKTIONEN.includes(optionen.aktion)) {
        maengel.push(`Aktionsart ${optionen.aktion} ist noch nicht ausführbar.`);
    }
    if (optionen.herkunft === "anliegen" &&
        INTERNE_AKTIONEN.includes(optionen.aktion)) {
        maengel.push("Interne Aktionsart nur durch Hausmeister erlaubt.");
    }
    if (optionen.aktion === "task_create" ||
        optionen.aktion === "aufgabe_anlegen") {
        if (!optionen.felder["projektId"]?.wert) {
            maengel.push("Aufgaben-Vorschlag verlangt ein Projekt.");
        }
        if (!optionen.felder["titel"]?.wert?.trim()) {
            maengel.push("Aufgaben-Vorschlag verlangt einen Titel.");
        }
    }
    if (optionen.aktion === "seite_anlegen") {
        if (!optionen.felder["projektId"]?.wert) {
            maengel.push("Seiten-Vorschlag verlangt ein Projekt.");
        }
        if (!optionen.felder["titel"]?.wert?.trim()) {
            maengel.push("Seiten-Vorschlag verlangt einen Titel.");
        }
    }
    if ((optionen.aktion === "acceptance_criterion" ||
        optionen.aktion === "akzeptanzkriterium_anlegen") &&
        !optionen.felder["aufgabeId"]?.wert) {
        maengel.push("Akzeptanzkriterium verlangt eine Aufgabe als Ziel.");
    }
    if (optionen.aktion === "akzeptanzkriterium_anlegen" &&
        !optionen.felder["text"]?.wert?.trim()) {
        maengel.push("Akzeptanzkriterium verlangt einen Text.");
    }
    if (optionen.aktion === "meilenstein_anlegen" ||
        optionen.aktion === "milestone_create") {
        if (!optionen.felder["projektId"]?.wert) {
            maengel.push("Meilenstein-Vorschlag verlangt ein Projekt.");
        }
        if (!optionen.felder["faelligAm"]?.wert) {
            maengel.push("Meilenstein-Vorschlag verlangt eine Fälligkeit.");
        }
    }
    for (const [name, beleg] of Object.entries(optionen.herleitung)) {
        if (!pruefeHerleitung(optionen.satz, beleg)) {
            maengel.push(`Herleitung für ${name} steht nicht wörtlich im Satz.`);
        }
    }
    for (const [name, feld] of Object.entries(optionen.felder)) {
        maengel.push(...pruefeFeld(feld).map((m) => `${name}: ${m}`));
    }
    return maengel;
}
/** Anzahl der Fortschrittsstufen vor Abschluss — Warteschlange 4, Beiwagen 3. */
export function fortschrittStufen(weg) {
    return weg === "warteschlange"
        ? FORTSCHRITT_WARTESCHLANGE.length
        : FORTSCHRITT_BEIWAGEN.length;
}
/**
 * Doppelte Vorschläge im selben Lauf erkennen (gleiche Aktion + gleiche Felder).
 */
export function istDoppelterVorschlag(bestehend, kandidat) {
    const signatur = (v) => JSON.stringify({
        aktion: v.aktion,
        felder: Object.fromEntries(Object.entries(v.felder)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, f]) => [k, f.wert])),
    });
    const ziel = signatur(kandidat);
    return bestehend.some((v) => signatur(v) === ziel);
}
//# sourceMappingURL=metarr.js.map